import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import { useCart } from '../shared/context/CartContext';
import { productApi, settingsApi } from '../shared/api/endpoints';
import { CONTACT_DEFAULTS } from '../shared/constants/contactDefaults';
import { buildWhatsAppUrl, buildCartWhatsAppMessage, formatPhoneDisplay } from '../shared/utils/whatsapp';
import { useAddToCart } from '../shared/hooks/useAddToCart';
import ValidatedInput from '../../validations/ValidatedInput';
import './CartPage.css';

const FREE_SHIP_THRESHOLD = 2999;

function WhatsAppIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function CartPage() {
  const { items, updateItem, removeItem } = useCart();
  const addToCart = useAddToCart();
  const [recs, setRecs] = useState([]);
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    channel: 'whatsapp',
  });
  const [agreed, setAgreed] = useState(false);
  const [whatsapp, setWhatsapp] = useState(null);
  const [contactInfo, setContactInfo] = useState({
    email: CONTACT_DEFAULTS.email,
    hours: CONTACT_DEFAULTS.hours,
  });
  const [error, setError] = useState('');

  const total = items.reduce((sum, i) => {
    const price = i.product?.sellingPrice || 0;
    return sum + price * (i.quantity || 1);
  }, 0);

  const subtotalBeforeDiscount = items.reduce((sum, i) => {
    const p = i.product || {};
    const price = p.actualPrice > p.sellingPrice ? p.actualPrice : p.sellingPrice || 0;
    return sum + price * (i.quantity || 1);
  }, 0);

  const discount = Math.max(0, subtotalBeforeDiscount - total);
  const count = items.reduce((sum, i) => sum + (i.quantity || 1), 0);
  const freeShipping = total >= FREE_SHIP_THRESHOLD;

  const onContactChange = (e) => {
    const { name, value } = e.target;
    setContact((c) => ({ ...c, [name]: value }));
  };

  const whatsappHref = useMemo(() => {
    if (!whatsapp?.number) return '';
    const message = buildCartWhatsAppMessage(whatsapp, items, contact);
    return buildWhatsAppUrl(whatsapp.number, message);
  }, [whatsapp, items, contact]);

  useEffect(() => {
    settingsApi
      .get()
      .then((res) => {
        const data = res.data?.data || {};
        setWhatsapp(data.whatsapp || null);
        setContactInfo({
          email: data.contactEmail || CONTACT_DEFAULTS.email,
          hours: data.contactHours || CONTACT_DEFAULTS.hours,
        });
      })
      .catch(() => {
        setWhatsapp(null);
      });
  }, []);

  useEffect(() => {
    productApi
      .list({ limit: 6 })
      .then((res) => {
        const all = res.data.data?.items || [];
        const inCart = new Set(items.map((i) => String(i.productId || i.product?._id)));
        setRecs(all.filter((p) => !inCart.has(String(p._id))).slice(0, 4));
      })
      .catch(() => setRecs([]));
  }, [items]);

  return (
    <div className="page-shell">
      <Navbar />

      <main className="cart">
        <div className="cart__shell">
          {items.length === 0 ? (
            <div className="cart__empty">
              <div className="cart__empty-card">
                <p>Your cart is empty</p>
                <span>Add pieces you love — our designer will help you perfect the fit.</span>
                <Link to="/products" className="cart__cta">
                  Shop now
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="cart__layout">
                <div className="cart__forms">
                  <section className="cart-block">
                    <h2 className="cart-block__title">Contact information</h2>
                    <p className="cart-block__lead">
                      Share your details so our atelier designer can reach you about sizing, custom
                      work, and delivery.
                    </p>
                    <div className="cart-field-row">
                      <label className="cart-field">
                        <span>First name</span>
                        <ValidatedInput
                          kind="text"
                          name="firstName"
                          value={contact.firstName}
                          onChange={onContactChange}
                          placeholder="First name"
                          autoComplete="given-name"
                        />
                      </label>
                      <label className="cart-field">
                        <span>Last name</span>
                        <ValidatedInput
                          kind="text"
                          name="lastName"
                          value={contact.lastName}
                          onChange={onContactChange}
                          placeholder="Last name"
                          autoComplete="family-name"
                        />
                      </label>
                    </div>
                    <div className="cart-field-row">
                      <label className="cart-field">
                        <span>Email address</span>
                        <ValidatedInput
                          kind="email"
                          name="email"
                          value={contact.email}
                          onChange={onContactChange}
                          placeholder="you@email.com"
                          autoComplete="email"
                        />
                      </label>
                      <label className="cart-field">
                        <span>Phone number (10 digits)</span>
                        <ValidatedInput
                          kind="phone"
                          name="phone"
                          value={contact.phone}
                          onChange={onContactChange}
                          placeholder="9876543210"
                          autoComplete="tel"
                        />
                      </label>
                    </div>
                  </section>

                  <section className="cart-block">
                    <h2 className="cart-block__title">Connect with your designer</h2>
                    <p className="cart-block__lead">
                      Every Nayavarnam order is guided by our studio team — no online checkout, just a
                      personal conversation.
                    </p>
                    <ul className="cart-channels" role="radiogroup" aria-label="Preferred contact">
                      {[
                        { id: 'whatsapp', label: 'WhatsApp — share photos & quick replies' },
                        { id: 'phone', label: 'Phone call — walk through your bag' },
                        { id: 'email', label: 'Email — detailed notes & timelines' },
                      ].map((opt) => (
                        <li key={opt.id}>
                          <label className="cart-channel">
                            <input
                              type="radio"
                              name="channel"
                              value={opt.id}
                              checked={contact.channel === opt.id}
                              onChange={onContactChange}
                            />
                            <span className="cart-channel__dot" aria-hidden />
                            <span>{opt.label}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                    <label className="cart-field cart-field--full">
                      <span>Message for the designer (optional)</span>
                      <ValidatedInput
                        kind="textarea"
                        name="message"
                        value={contact.message}
                        onChange={onContactChange}
                        rows={4}
                        placeholder="Occasion, preferred delivery window, or custom motif ideas…"
                      />
                    </label>
                    <div className="cart-designer-card">
                      <div className="cart-designer-card__avatar" aria-hidden>
                        NV
                      </div>
                      <div>
                        <strong>Atelier stylist</strong>
                        <p>
                          {contactInfo.email}
                          {whatsapp?.number ? ` · ${formatPhoneDisplay(whatsapp.number)}` : ''}
                        </p>
                        {contactInfo.hours && (
                          <p className="cart-designer-card__hours">Studio hours {contactInfo.hours}</p>
                        )}
                      </div>
                    </div>
                  </section>
                </div>

                <aside className="cart__order" aria-label="Order summary">
                  <h2 className="cart__order-title">Order summary</h2>
                  <ul className="cart-order-list">
                    {items.map((item) => {
                      const p = item.product || {};
                      const id = item.productId || p._id;
                      const qty = item.quantity || 1;
                      const line = (p.sellingPrice || 0) * qty;
                      const was = p.actualPrice > p.sellingPrice ? p.actualPrice * qty : null;
                      const color = p.color || p.colors?.[0];
                      return (
                        <li key={`${id}-${item.size}`} className="cart-order-item">
                          <Link
                            to={`/products/${id}`}
                            className="cart-order-item__thumb"
                            style={{
                              backgroundImage: p.images?.[0] ? `url(${p.images[0]})` : undefined,
                            }}
                            aria-label={p.title || 'Product'}
                          />
                          <div className="cart-order-item__main">
                            <div className="cart-order-item__head">
                              <div>
                                <Link to={`/products/${id}`}>
                                  <h3>{p.title || 'Product'}</h3>
                                </Link>
                                {p.productCode && (
                                  <p className="cart-order-item__code">Art. {p.productCode}</p>
                                )}
                                <p className="cart-order-item__meta">
                                  {item.size ? `Size ${item.size}` : 'One size'}
                                  {color ? (
                                    <>
                                      <span aria-hidden>·</span>
                                      <span
                                        className="cart-order-item__swatch"
                                        style={{ background: color }}
                                      />
                                    </>
                                  ) : null}
                                </p>
                              </div>
                              <div className="cart-order-item__prices">
                                <strong>₹{Number(line).toLocaleString()}</strong>
                                {was != null && (
                                  <s>₹{Number(was).toLocaleString()}</s>
                                )}
                              </div>
                            </div>
                            <div className="cart-order-item__foot">
                              <div className="cart-order-item__qty" aria-label="Quantity">
                                <button
                                  type="button"
                                  onClick={() => updateItem(id, item.size || '', qty - 1)}
                                  aria-label="Decrease quantity"
                                >
                                  −
                                </button>
                                <span>{qty}</span>
                                <button
                                  type="button"
                                  onClick={() => updateItem(id, item.size || '', qty + 1)}
                                  aria-label="Increase quantity"
                                >
                                  +
                                </button>
                              </div>
                              <button
                                type="button"
                                className="cart-order-item__remove"
                                onClick={() => removeItem(id, item.size || '')}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="cart-totals">
                    <div className="cart-totals__row">
                      <span>Subtotal</span>
                      <strong>₹{subtotalBeforeDiscount.toLocaleString()}</strong>
                    </div>
                    {discount > 0 && (
                      <div className="cart-totals__row cart-totals__row--muted">
                        <span>Discount</span>
                        <span>− ₹{discount.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="cart-totals__row cart-totals__row--muted">
                      <span>Styling consultation</span>
                      <span>Free</span>
                    </div>
                    <div className="cart-totals__row cart-totals__row--muted">
                      <span>Delivery</span>
                      <span>{freeShipping ? 'Complimentary' : 'Discuss with designer'}</span>
                    </div>
                    <div className="cart-totals__total">
                      <span>Estimated total</span>
                      <strong>₹{total.toLocaleString()}</strong>
                    </div>
                  </div>

                  <a
                    href={whatsappHref || '#'}
                    className={`cart__whatsapp${!whatsappHref ? ' is-disabled' : ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-disabled={!whatsappHref}
                    onClick={(e) => {
                      if (!whatsappHref) {
                        e.preventDefault();
                        setError('WhatsApp is not configured. Set WHATSAPP_NUMBER in server .env and restart the server.');
                      }
                    }}
                  >
                    <WhatsAppIcon />
                    Continue on WhatsApp
                  </a>

                  {error && <p className="cart__error">{error}</p>}

                  <label className="cart__terms">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                    />
                    <span>I agree to be contacted about this bag by Nayavarnam.</span>
                  </label>

                  <p className="cart__note">
                    {count} {count === 1 ? 'piece' : 'pieces'} in your bag — final payment and shipping
                    are arranged directly with your designer.
                  </p>
                </aside>
              </div>

              {recs.length > 0 && (
                <section className="cart-recs">
                  <div className="cart-recs__head">
                    <h2>You may also love</h2>
                    <p>Curated picks from the atelier.</p>
                  </div>
                  <div className="cart-recs__grid">
                    {recs.map((p) => (
                      <article key={p._id} className="cart-rec">
                        <Link
                          to={`/products/${p._id}`}
                          className="cart-rec__media"
                          style={{
                            backgroundImage: p.images?.[0] ? `url(${p.images[0]})` : undefined,
                          }}
                        />
                        <div className="cart-rec__body">
                          <h3>{p.title}</h3>
                          <strong>₹{Number(p.sellingPrice || 0).toLocaleString()}</strong>
                          <button
                            type="button"
                            className="cart-rec__add"
                            onClick={() => addToCart(p, p.sizes?.[0] || p.sizesAvailable?.[0] || '', 1)}
                          >
                            Quick add
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
